/**
 * Client-side ZK proving helper for the circom/snarkjs circuits (anonymous
 * handles, perps ownership auth).
 *
 * Every artifact fetched over the network is verified against the SHA-256
 * pins below before it is handed to snarkjs — the same fail-closed pattern
 * the JoinSplit browser prover uses for its key manifest. A CDN or hosting
 * compromise can therefore deny service but never substitute a circuit or
 * proving key. The pins are the committed values from
 * privacy-protocol/circuits/artifacts (anonymous_handles_manifest.json and
 * the perps export manifest); rebuilds of the circuits must update both.
 */
import { ARTIFACT_VERSION } from "../config";

/**
 * The circuit artifacts ship with the app itself (public/artifacts/…): they
 * are integrity-pinned below, so same-origin static hosting is both the
 * simplest and the safest source — no prover host needs to be running for
 * the browser to prove. An env override remains for split deployments.
 */
const CIRCOM_ARTIFACTS_BASE_URL =
  process.env.REACT_APP_CIRCOM_ARTIFACTS_URL || "/artifacts";

interface CircuitArtifactPins {
  wasm: string;
  zkey: string;
  verificationKey: string;
}

const PINNED_CIRCUIT_ARTIFACTS: Record<string, CircuitArtifactPins> = {
  handle_register: {
    wasm: "05d5da57ccb59354b53ea760ece6952950324d157fbd68f059e5d3f8fd3d4c6d",
    zkey: "fcb78854d61b190065a57aec098d7adb37bc92206e641f36c28294d164dae0b6",
    verificationKey:
      "1985842abe6874e59645561faaabbcbf1e3171765b797c60347923ecd3276fc3",
  },
  handle_rotate: {
    wasm: "89786db83008e14e5ae3d366a6d07ed3e5a17d87d978ae627f3087cdb94734ae",
    zkey: "38fbb51d014567c2826f6b5527b93429d28372a2678a6a5cc221df24f8059a40",
    verificationKey:
      "9dc39ae5cbe05e57534512dcaa1b1fff2619d1d26af14e341ef046621c6411b1",
  },
  perps_ownership_auth: {
    wasm: "da942c798edead35d916d9bbdf31d2776f219ea373e552519394dfa5001b2458",
    zkey: "2ef324971d2716c782ee8fda5da0edfe52d235a70de2532136bdc06e1a07f388",
    verificationKey:
      "30e143aed0e17c36b260f14c43d1eeb5cdf6aba4e4bf3b2f39d4c2cc2071468b",
  },
};

const artifactCache: Map<string, { wasm: Uint8Array; zkey: Uint8Array }> =
  new Map();

async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function requirePins(circuit: string): CircuitArtifactPins {
  const pins = PINNED_CIRCUIT_ARTIFACTS[circuit];
  if (!pins) {
    // Refusing outright beats proving with whatever the host serves.
    throw new Error(`No pinned artifact hashes for circuit "${circuit}"`);
  }
  return pins;
}

async function fetchPinned(
  url: string,
  expectedSha256: string,
  label: string,
): Promise<ArrayBuffer> {
  const response = await fetch(url, { cache: "no-cache" });
  if (!response.ok) {
    throw new Error(`Failed to fetch ${label} (HTTP ${response.status})`);
  }
  const bytes = await response.arrayBuffer();
  const actual = await sha256Hex(bytes);
  if (actual !== expectedSha256.toLowerCase()) {
    throw new Error(
      `${label} failed its integrity check; refusing to prove with it`,
    );
  }
  return bytes;
}

export async function fetchArtifacts(
  circuit: string,
  artifactsBaseUrl: string = CIRCOM_ARTIFACTS_BASE_URL,
): Promise<{ wasm: Uint8Array; zkey: Uint8Array }> {
  const cacheKey = `${artifactsBaseUrl}/${circuit}`;
  if (artifactCache.has(cacheKey)) {
    return artifactCache.get(cacheKey)!;
  }

  const pins = requirePins(circuit);
  // Cache-buster keeps CDN caches from serving a stale artifact after a
  // rebuild; the hash pin is what actually guarantees integrity.
  const cacheBuster = `?v=${ARTIFACT_VERSION}`;
  const [wasmBuf, zkeyBuf] = await Promise.all([
    fetchPinned(
      `${artifactsBaseUrl}/${circuit}/${circuit}.wasm${cacheBuster}`,
      pins.wasm,
      `${circuit}.wasm`,
    ),
    fetchPinned(
      `${artifactsBaseUrl}/${circuit}/${circuit}.zkey${cacheBuster}`,
      pins.zkey,
      `${circuit}.zkey`,
    ),
  ]);

  const artifacts = {
    wasm: new Uint8Array(wasmBuf),
    zkey: new Uint8Array(zkeyBuf),
  };

  artifactCache.set(cacheKey, artifacts);
  return artifacts;
}

/** Pin-checked verification key, for verifying a freshly generated proof locally. */
export async function fetchVerificationKey(
  circuit: string,
  artifactsBaseUrl: string = CIRCOM_ARTIFACTS_BASE_URL,
): Promise<unknown> {
  const pins = requirePins(circuit);
  const bytes = await fetchPinned(
    `${artifactsBaseUrl}/${circuit}/verification_key.json?v=${ARTIFACT_VERSION}`,
    pins.verificationKey,
    `${circuit} verification key`,
  );
  return JSON.parse(new TextDecoder().decode(bytes));
}

type CircuitInputValue = string | number | bigint | CircuitInputValue[];
type CircuitInputs = Record<string, CircuitInputValue>;

export async function generateProofClientSide(
  circuit: string,
  inputs: CircuitInputs,
  artifactsBaseUrl?: string,
): Promise<{ proof: unknown; publicSignals: string[] }> {
  const snarkjs = await import("snarkjs");
  const artifacts = await fetchArtifacts(circuit, artifactsBaseUrl);

  const startTime = performance.now();
  // snarkjs accepts various input types including arrays for multi-input circuits
  const { proof, publicSignals } = await snarkjs.groth16.fullProve(
    inputs as unknown as Record<string, string | number | bigint>,
    artifacts.wasm,
    artifacts.zkey,
    undefined,
    undefined,
    // ffjavascript's default pool creates data-URL workers, which the app's
    // worker-src 'self' policy correctly blocks. These small Circom circuits
    // use local single-threaded WASM without weakening that policy.
    { singleThread: true },
  );
  const elapsed = Math.round(performance.now() - startTime);

  console.log(`[snarkjs] ${circuit} proof generated in ${elapsed}ms`);
  return { proof, publicSignals };
}
